import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { DiscountFreeRangeLogRow, DiscountFreeRangeRow } from '../../../../../core/models/parking/discount-free-range.model';
import { MapperDiscountFreeRangeService } from '../../../../../core/mappers/parking/mapper-discount-free-range.service';
import { ApiDiscountFreeRangeService } from '../../../../../core/services/api/parking/api-discount-free-range.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

export const FREE_RANGE_DATE_FORMATS = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearLabel: { year: 'numeric', month: 'short' } as Intl.DateTimeFormatOptions,
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearA11yLabel: { year: 'numeric', month: 'long' } as Intl.DateTimeFormatOptions,
  },
};

@Component({
  selector: 'app-modal-discount-free-range',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDatepickerModule, MatNativeDateModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-discount-free-range.component.html',
  styleUrl: './modal-discount-free-range.component.css',
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: FREE_RANGE_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
  ],
})
export class ModalDiscountFreeRangeComponent implements OnInit {
  @Input() id = 0;
  @Input() siteId = 0;
  @Input() siteName = '';
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  private readonly api = inject(ApiDiscountFreeRangeService);
  private readonly mapper = inject(MapperDiscountFreeRangeService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  isSaving = signal(false);
  isLoading = signal(false);
  logs = signal<DiscountFreeRangeLogRow[]>([]);
  licensePlate = '';
  startDate: Date | null = null;
  endDate: Date | null = null;
  remark = '';
  taiwanUuid = '';
  canEdit = true;
  isEnabled = true;
  detail: DiscountFreeRangeRow | null = null;

  get isEdit(): boolean {
    return this.id > 0;
  }

  get isLocked(): boolean {
    return this.isEdit && !this.canEdit;
  }

  ngOnInit(): void {
    if (this.isEdit) this.loadDetail();
  }

  onClose(): void {
    if (this.isSaving()) return;
    this.close.emit();
  }

  save(): void {
    if (this.isLocked) return;
    const { userId, menuId } = this.auth();
    if (!userId || !menuId) return;
    if (!this.siteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    if (!this.isEdit && !this.licensePlate.trim()) {
      this.toast.error('กรุณาระบุทะเบียนรถ');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาเลือกวันเริ่มต้นและวันสิ้นสุด');
      return;
    }
    const start = this.mapper.combineDateTime(this.startDate, this.startTime);
    const end = this.mapper.combineDateTime(this.endDate, this.endTime);
    if (!start || !end) {
      this.toast.error('กรุณาระบุเวลาให้ถูกต้อง (HH:mm แบบ 24 ชั่วโมง)');
      return;
    }
    if (end <= start) {
      this.toast.error('วันเวลาสิ้นสุดต้องหลังวันเวลาเริ่มต้น');
      return;
    }

    this.isSaving.set(true);
    const startDate = this.mapper.toYmdHm(start);
    const endDate = this.mapper.toYmdHm(end);
    const remark = this.remark.trim() || null;

    if (this.isEdit) {
      this.api.updateDiscountFreeRange({
        userId,
        menuId,
        id: this.id,
        startDate,
        endDate,
        remark,
      }).subscribe({
        next: (res) => this.handleSave(res),
        error: () => {
          this.isSaving.set(false);
          this.toast.error('แก้ไขไม่สำเร็จ');
        },
      });
      return;
    }

    this.api.createDiscountFreeRange({
      userId,
      menuId,
      siteId: this.siteId,
      licensePlate: this.licensePlate.trim(),
      startDate,
      endDate,
      remark,
      isEnabled: true,
    }).subscribe({
      next: (res) => this.handleSave(res),
      error: () => {
        this.isSaving.set(false);
        this.toast.error('สร้างไม่สำเร็จ');
      },
    });
  }

  private loadDetail(): void {
    this.isLoading.set(true);
    this.api.getDiscountFreeRangeById(this.id).subscribe({
      next: (res) => {
        this.detail = this.mapper.mapRow(res);
        this.licensePlate = this.detail.licensePlate;
        this.startDate = this.mapper.parseDate(this.detail.startDate);
        this.endDate = this.mapper.parseDate(this.detail.endDate);
        this.startTime = this.mapper.toHm(this.startDate) || '00:00';
        this.endTime = this.mapper.toHm(this.endDate) || '23:59';
        this.remark = this.detail.remark;
        this.taiwanUuid = this.detail.taiwanUuid;
        this.canEdit = this.detail.canEdit;
        this.isEnabled = this.detail.isEnabled;
        this.isLoading.set(false);
        this.loadLogs();
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('โหลดรายการไม่สำเร็จ');
      },
    });
  }

  private loadLogs(): void {
    this.api.getDiscountFreeRangeLogs(this.id, 1, 20).subscribe({
      next: (res) => this.logs.set(this.mapper.mapLogs(res).data),
      error: () => this.logs.set([]),
    });
  }

  private handleSave(res: unknown): void {
    const mapped = this.mapper.mapActionResponse(res);
    this.isSaving.set(false);
    if (mapped.isSuccess) {
      this.toast.success(mapped.message || 'บันทึกสำเร็จ');
      this.saveSuccess.emit();
      this.close.emit();
      return;
    }
    this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
  }

  private auth(): { userId: number; menuId: number } {
    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;
    if (!userId || !menuId) this.toast.error('ไม่พบข้อมูลสิทธิ์');
    return { userId, menuId };
  }

  get canShowActivityHistory(): boolean {
    return this.id > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }

  // --- เวลา (HH:mm 24 ชม.) ---
  startTime = '00:00';
  endTime = '23:59';
  readonly timePresets = ['00:00', '08:00', '12:00', '18:00', '23:59'];

  onTimeBlur(field: 'start' | 'end'): void {
    const current = field === 'start' ? this.startTime : this.endTime;
    const normalized = this.mapper.normalizeHm(current);
    if (!normalized) {
      if (current.trim()) this.toast.error('รูปแบบเวลาไม่ถูกต้อง ใช้ HH:mm เช่น 08:30');
      return;
    }
    if (field === 'start') this.startTime = normalized;
    else this.endTime = normalized;
  }

  setTime(field: 'start' | 'end', value: string): void {
    if (this.isLocked || this.isSaving()) return;
    if (field === 'start') this.startTime = value;
    else this.endTime = value;
  }

  get rangePreview(): string {
    const start = this.mapper.combineDateTime(this.startDate, this.startTime);
    const end = this.mapper.combineDateTime(this.endDate, this.endTime);
    if (!start || !end) return '';
    return `${this.mapper.formatDisplayDateTime(start.toISOString())} – ${this.mapper.formatDisplayDateTime(end.toISOString())}`;
  }

  get rangeInvalid(): boolean {
    const start = this.mapper.combineDateTime(this.startDate, this.startTime);
    const end = this.mapper.combineDateTime(this.endDate, this.endTime);
    return !!start && !!end && end <= start;
  }
}
